<template>
  <div class="forms-container">
    <div class="list-header">
      <h2 class="list-title">{{ t('registration.myForms') }}</h2>
      <button @click="onCreate" class="btn btn-primary">
        <span class="btn-icon">➕</span>
        {{ t('registration.createForm') }}
      </button>
    </div>

    <div v-if="registrationForms.length === 0" class="empty-state">
      <div class="empty-icon">📝</div>
      <h3>{{ t('registration.noForms') }}</h3>
      <p>{{ t('registration.noFormsDescription') }}</p>
    </div>

    <div v-else class="forms-grid">
      <table class="forms-table">
        <thead>
          <tr>
            <th>{{ t('registration.formName') }}</th>
            <th>{{ t('registration.description') }}</th>
            <th>{{ t('general.status') }}</th>
            <th>{{ t('registration.fields') }}</th>
            <th>{{ t('registration.submissions') }}</th>
            <th>{{ t('general.created') }}</th>
            <th class="embed-column">{{ t('general.embed') || 'Embed' }}</th>
            <th>{{ t('general.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="form in registrationForms"
            :key="form.id"
            :class="{ inactive: !form.isActive }"
          >
            <td class="form-title-cell">
              {{ form.title || form.name }}
            </td>
            <td class="form-description-cell truncate">
              {{ form.description || '-' }}
            </td>
            <td>
              <span
                class="status-badge"
                :class="form.isActive ? 'active' : 'inactive'"
              >
                {{
                  form.isActive ? t('general.active') : t('general.inactive')
                }}
              </span>
            </td>
            <td class="meta-value">{{ form.fields.length }}</td>
            <td class="meta-value">{{ form.submissionCount }}</td>
            <td class="date-cell">{{ formatDate(form.createdAt) }}</td>
            <td class="embed-cell">
              <button
                @click="emit('show-embed', form)"
                class="btn btn-primary"
                :title="t('general.embedCode')"
              >
                <span class="btn-icon">🔗</span>
              </button>
            </td>
            <td>
              <div class="form-actions">
                <button
                  @click="emit('edit', form)"
                  class="btn btn-secondary"
                  :title="t('general.edit')"
                >
                  <span class="btn-icon">✏️</span>
                </button>
                <button
                  @click="emit('copy', form)"
                  class="btn btn-secondary"
                  :title="t('button.copy')"
                >
                  <span class="btn-icon">📄</span>
                </button>
                <button
                  @click="emit('view', form)"
                  class="btn btn-secondary"
                  :title="t('general.view')"
                >
                  <span class="btn-icon">👁️</span>
                </button>
                <button
                  @click="emit('delete', form.id)"
                  class="btn btn-danger"
                  :title="t('general.delete')"
                >
                  <span class="btn-icon">🗑️</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'

export default {
  name: 'FormsList',
  props: {
    registrationForms: { type: Array, required: true },
  },
  emits: ['create', 'edit', 'copy', 'delete', 'show-embed', 'view'],
  setup(props, { emit }) {
    const { t } = useI18n()

    const onCreate = () => emit('create')

    const formatDate = (dateString) => {
      if (!dateString) return t('general.notSet')
      const date = new Date(dateString)
      return date.toLocaleDateString('et-EE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    }

    return { t, emit, onCreate, formatDate }
  },
}
</script>

<style scoped>
@import './list-styles.css';
.forms-container {
  padding: 1rem;
  margin: 0;
}

.header {
  text-align: center;
  margin-bottom: 1.5rem;
  position: relative;
}

.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.empty-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
}
.empty-state h3 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
}
.empty-state p {
  color: #6c757d;
  font-size: 0.9rem;
}

/* Lists use shared styles via @import; keep only component-specific rules below */
.forms-table .embed-column {
  width: 1%;
  white-space: nowrap;
  text-align: center;
}

.forms-table .embed-cell {
  text-align: center;
}
</style>
